<template>
  <div
    ref="containerRef"
    class="relative w-full"
  >
    <!-- Input wrapper -->
    <div class="relative w-full">
      <span class="absolute left-3 top-0 bottom-0 z-10 flex items-center pointer-events-none">
        <i
          :class="[
            'text-sm',
            isLoading ? 'pi pi-spinner pi-spin text-ocv-primary-500' : 'pi pi-search text-slate-400',
          ]"
          aria-hidden="true"
        />
      </span>
      <InputText
        ref="inputRef"
        v-model="query"
        type="text"
        placeholder="Rechercher un commerçant, marché, placier, facture…"
        autocomplete="off"
        aria-label="Recherche globale"
        :aria-expanded="hasResults && query.length >= 2"
        aria-haspopup="listbox"
        class="search-input w-full text-sm"
        :pt="{
          root: {
            class:
              'pl-9 pr-9 py-2 rounded-md border border-slate-200 bg-slate-50 text-slate-800 placeholder-slate-400 transition focus:outline-none focus:border-ocv-primary-500 focus:bg-white text-left shadow-none',
          },
        }"
        @focus="open"
        @keydown.escape="handleEscape"
        @keydown.enter.prevent="handleEnterKey"
        @keydown.arrow-down.prevent="moveFocus(1)"
        @keydown.arrow-up.prevent="moveFocus(-1)"
      />
      <button
        v-if="query.length > 0"
        type="button"
        class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition z-10"
        aria-label="Effacer la recherche"
        @click="clearSearch"
      >
        <i
          class="pi pi-times text-xs"
          aria-hidden="true"
        />
      </button>
    </div>

    <!-- Results dropdown -->
    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0 translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-1"
    >
      <div
        v-if="isOpen && query.length >= 2"
        role="listbox"
        aria-label="Résultats de recherche"
        class="search-dropdown"
      >
        <!-- Loading -->
        <div
          v-if="isLoading && !hasResults"
          class="flex flex-col items-center justify-center py-10 text-slate-400"
        >
          <i
            class="pi pi-spinner pi-spin text-2xl mb-2 text-ocv-primary-500"
            aria-hidden="true"
          />
          <p class="text-sm text-slate-500">
            Recherche…
          </p>
        </div>

        <!-- No results -->
        <div
          v-else-if="!hasResults"
          class="flex flex-col items-center justify-center py-10 text-slate-400"
        >
          <i
            class="pi pi-search text-3xl mb-3 opacity-40"
            aria-hidden="true"
          />
          <p class="text-sm font-medium text-slate-500">
            Aucun résultat pour &laquo;&nbsp;{{ query }}&nbsp;&raquo;
          </p>
          <p class="text-xs text-slate-400 mt-1">
            Essayez un autre terme
          </p>
        </div>

        <!-- Grouped results -->
        <template v-else>
          <div class="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
            <span class="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Résultats
            </span>
            <span class="text-xs text-slate-400">{{ totalCount }} trouvé{{ totalCount > 1 ? 's' : '' }}</span>
          </div>

          <div
            v-for="(items, category) in results"
            :key="category"
          >
            <!-- Category header -->
            <div class="flex items-center gap-2 px-3 pt-3 pb-1">
              <i
                :class="[categoryIcons[category], 'text-xs text-ocv-primary-500']"
                aria-hidden="true"
              />
              <span class="text-xs font-semibold text-ocv-primary-700 uppercase tracking-wider">
                {{ category }}
              </span>
            </div>

            <!-- Items -->
            <button
              v-for="item in items"
              :key="item.id"
              :ref="(el: unknown) => setItemRef(el, item.id)"
              role="option"
              :aria-selected="focusedItem?.id === item.id"
              class="w-full flex items-center gap-3 px-3 py-2.5 text-left transition hover:bg-slate-50 focus:bg-slate-50 focus:outline-none"
              :class="{ 'bg-ocv-primary-50': focusedItem?.id === item.id }"
              @click="selectResult(item)"
              @mouseenter="focusedItem = item"
            >
              <span
                class="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100"
                :class="{ 'bg-ocv-primary-100': focusedItem?.id === item.id }"
              >
                <i
                  :class="[item.icon, 'text-sm']"
                  :style="{ color: focusedItem?.id === item.id ? 'var(--ocv-primary-500)' : '#64748b' }"
                  aria-hidden="true"
                />
              </span>
              <span class="flex-1 min-w-0">
                <!-- Libellé échappé côté script (escapeHtml) : le v-html est sûr, seul le <mark> inséré est du HTML. -->
                <!-- eslint-disable vue/no-v-html -->
                <span
                  class="block text-sm font-medium text-slate-800 truncate"
                  v-html="highlightMatch(item.label)"
                />
                <!-- eslint-enable vue/no-v-html -->

                <span
                  v-if="item.description"
                  class="block text-xs text-slate-400 truncate"
                >{{ item.description }}</span>
              </span>
            </button>
          </div>

          <!-- Footer hint -->
          <div class="px-3 py-2 border-t border-slate-100 flex items-center gap-3 text-xs text-slate-400">
            <span><kbd class="font-mono bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded text-[10px]">↑↓</kbd> naviguer</span>
            <span><kbd class="font-mono bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded text-[10px]">↵</kbd> sélectionner</span>
            <span><kbd class="font-mono bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded text-[10px]">Esc</kbd> fermer</span>
          </div>
        </template>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, nextTick } from "vue";
import { useGlobalSearch, categoryIcons, type SearchResult } from "~/composables/useGlobalSearch";

const { query, results, hasResults, totalCount, isOpen, isLoading, open, close, clear } = useGlobalSearch();

const containerRef = ref<HTMLElement | null>(null);
const inputRef = ref<{ $el: HTMLInputElement } | null>(null);

function focusInput() {
  inputRef.value?.$el?.focus();
}

function blurInput() {
  inputRef.value?.$el?.blur();
}

// Flattened list of all results for keyboard navigation
const flatResults = computed<SearchResult[]>(() => Object.values(results.value).flat());
const focusedItem = ref<SearchResult | null>(null);

// Item refs map for scrolling, indexée par id de résultat (unique tous groupes
// confondus, comme `flatResults` utilisée par la navigation clavier).
const itemRefs = ref<Map<string, HTMLElement>>(new Map());

function setItemRef(el: unknown, id: string) {
  if (el instanceof HTMLElement) {
    itemRefs.value.set(id, el);
  }
  else {
    itemRefs.value.delete(id);
  }
}

watch(results, () => {
  focusedItem.value = null;
  itemRefs.value.clear();
});

// Échappe le HTML avant l'insertion via v-html (le libellé vient du backend :
// un nom d'entité ne doit jamais pouvoir injecter de balise). Seul le <mark>
// ci-dessous, que l'on contrôle, reste du HTML.
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function highlightMatch(text: string): string {
  const escaped = escapeHtml(text);
  const q = query.value.trim();
  if (!q) return escaped;
  const regex = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
  return escaped.replace(regex, "<mark class=\"bg-ocv-primary-100 text-ocv-primary-700 rounded px-0.5\">$1</mark>");
}

function clearSearch() {
  clear();
  nextTick(() => focusInput());
}

function handleEscape() {
  close();
  blurInput();
}

function moveFocus(direction: 1 | -1) {
  const flat = flatResults.value;
  if (flat.length === 0) return;

  const currentIdx = focusedItem.value ? flat.findIndex(r => r.id === focusedItem.value!.id) : -1;
  let nextIdx = currentIdx + direction;

  if (nextIdx < 0) nextIdx = flat.length - 1;
  if (nextIdx >= flat.length) nextIdx = 0;

  const next = flat[nextIdx];
  focusedItem.value = next;

  // L'input garde le focus DOM : seule une classe CSS marque la sélection, le
  // navigateur ne fait donc défiler personne. Passé la hauteur du dropdown,
  // les flèches sélectionneraient des résultats hors écran.
  itemRefs.value.get(next.id)?.scrollIntoView({ block: "nearest" });
}

function handleEnterKey() {
  if (focusedItem.value) {
    selectResult(focusedItem.value);
  }
}

function selectResult(item: SearchResult) {
  if (item.route) {
    navigateTo(item.route);
  }
  close();
  blurInput();
}

// Close on outside click
function handleOutsideClick(event: MouseEvent) {
  if (containerRef.value && !containerRef.value.contains(event.target as Node)) {
    close();
  }
}

onMounted(() => {
  document.addEventListener("mousedown", handleOutsideClick);
});

onUnmounted(() => {
  document.removeEventListener("mousedown", handleOutsideClick);
});
</script>

<style scoped>
.search-input {
  background-color: #f8fafc;
}
.search-input:focus {
  background-color: #ffffff;
}

.search-dropdown {
  position: absolute;
  z-index: 50;
  left: 0;
  right: 0;
  margin-top: 0.5rem;
  max-height: 70vh;
  overflow-y: auto;
  background-color: #ffffff;
  border: 1px solid rgb(226 232 240);
  border-radius: 0.75rem;
  box-shadow: 0 10px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
}
</style>
